import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShowChart,
} from '@mui/icons-material';
import { Stock } from '../../types';
import { StockNameManager } from '../../services/stockNameManager';
import {
  getTrendDirection,
  getTrendTextColor,
  getTrendColorSet,
} from '../../theme/taiwanTrendColors';

interface WatchlistSectionProps {
  stocks: Stock[];
  stateStocks?: Stock[]; // 新增 state.stocks 參數，用於獲取優化的股票名稱
}

const WatchlistSection: React.FC<WatchlistSectionProps> = ({ stocks, stateStocks = [] }) => {
  const formatLargeNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  if (stocks.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            關注股票
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => (window.location.href = "/#/watchlist")}
          >
            設定關注
          </Button>
        </Box>
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <ShowChart sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            尚未設定關注股票
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          關注股票
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => (window.location.href = "/#/watchlist")}
        >
          查看全部
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {stocks.slice(0, 6).map((stock) => {
          const direction = getTrendDirection(stock.changePercent);
          const trendColor = getTrendTextColor(stock.changePercent);
          const colorSet = getTrendColorSet(stock.changePercent);
          const TrendIconComponent = direction === 'down' ? TrendingDown : TrendingUp;
          
          return (
            <Box
              key={stock.symbol}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1.5,
                px: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: colorSet.light,
                  borderColor: colorSet.border,
                },
              }}
            >
              {/* Left - Stock Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                    {stock.symbol}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: '0.8rem',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {StockNameManager.getStockName(stock.symbol)}
                  </Typography>
                </Box>
              </Box>

              {/* Center - Volume */}
              <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                <Typography variant="caption" color="text.secondary">
                  量: {formatLargeNumber(stock.volume)}
                </Typography>
              </Box>

              {/* Right - Price and Change */}
              <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1 }}>
                  ${stock.currentPrice.toFixed(2)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
                  <TrendIconComponent sx={{ fontSize: 14, color: trendColor }} />
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: trendColor }}
                  >
                    {stock.changePercent >= 0 ? '+' : ''}{stock.changePercent.toFixed(2)}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {stocks.length > 6 && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            還有 {stocks.length - 6} 支股票
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default WatchlistSection;
